import React, { useState, useEffect } from 'react';
import supabase from './supabaseClient';
import { fuzzySearchPlants } from './plantSearchUtils';

const PlantDatabase = () => {
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const perPage = 50;
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    fetchPlants();
  }, []);

  const fetchPlants = async () => {
    setLoading(true);
    let all = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const { data } = await supabase.from('plants').select('*').order('id', { ascending: false }).range(from, from + pageSize - 1);
      if (!data || data.length === 0) break;
      all = all.concat(data);
      if (data.length < pageSize) break;
      from += pageSize;
    }
    setPlants(all);
    setLoading(false);
  };

  // Canonical names to suggest from: approved records only, so typos/one-offs
  // in pending data don't get suggested back to themselves.
  const knownPlants = plants.filter(p => p.status === 'approved' && p.common_name);

  const handleNameInputChange = (value) => {
    setEditForm({ ...editForm, common_name: value });
    if (value.trim().length > 0) {
      const matches = fuzzySearchPlants(value, knownPlants, 8);
      setNameSuggestions(matches);
      setShowNameSuggestions(true);
    } else {
      setNameSuggestions([]);
      setShowNameSuggestions(false);
    }
  };

  const selectNameSuggestion = (match) => {
    // Always sync genus and scientific_name to the matched canonical record,
    // so a selected name can't carry over a mismatched/inconsistent genus.
    const sourceRecord = knownPlants.find(p => p.common_name === match.common_name);
    setEditForm(prev => ({
      ...prev,
      common_name: match.common_name,
      genus: sourceRecord?.genus || match.genus || prev.genus,
      scientific_name: sourceRecord?.scientific_name || match.scientific_name || prev.scientific_name,
    }));
    setShowNameSuggestions(false);
    setNameSuggestions([]);
  };

  const startEdit = (plant) => {
    setEditingId(plant.id);
    setEditForm({ ...plant });
    setNameSuggestions([]);
    setShowNameSuggestions(false);
  };

  const saveEdit = async () => {
    setSaving(true);
    const { id, ...updateData } = editForm;
    const { error } = await supabase.from('plants').update(updateData).eq('id', editingId);
    if (!error) {
      setPlants(plants.map(p => p.id === editingId ? { ...p, ...updateData } : p));
      setEditingId(null);
    } else {
      alert('Error saving: ' + error.message);
    }
    setSaving(false);
  };

  const deletePlant = async (id) => {
    if (!window.confirm('Permanently delete this plant record? This cannot be undone.')) return;
    const { error } = await supabase.from('plants').delete().eq('id', id);
    if (!error) setPlants(plants.filter(p => p.id !== id));
  };

  const uploadEditPhoto = async (file) => {
    if (!file || !editingId) return;
    setPhotoUploading(true);
    const fileExt = file.name.split('.').pop().toLowerCase();
    const safeExt = ['jpg','jpeg','png','webp','gif'].includes(fileExt) ? fileExt : 'jpg';
    const fileName = `${Date.now()}_db_${editingId}.${safeExt}`;
    const { error: uploadError } = await supabase.storage
      .from('plant-images')
      .upload(fileName, file, { contentType: file.type || 'image/jpeg' });
    if (uploadError) {
      alert('Upload failed: ' + uploadError.message);
      setPhotoUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('plant-images').getPublicUrl(fileName);
    setEditForm(prev => ({ ...prev, image_url: urlData.publicUrl }));
    setPhotoUploading(false);
  };

  const removeEditPhoto = () => {
    setEditForm(prev => ({ ...prev, image_url: '' }));
  };

  const filtered = plants.filter(p => {
    const matchesSearch = !searchQuery || (p.common_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.scientific_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.genus || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (sortField === 'sold_price') {
      valA = parseFloat(String(valA || '0').replace(/[^0-9.]/g, '')) || 0;
      valB = parseFloat(String(valB || '0').replace(/[^0-9.]/g, '')) || 0;
    } else if (sortField === 'date_sold') {
      valA = valA ? new Date(valA).getTime() : 0;
      valB = valB ? new Date(valB).getTime() : 0;
    } else if (sortField === 'id') {
      valA = valA || 0;
      valB = valB || 0;
    } else {
      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
    }

    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'id' || field === 'date_sold' || field === 'sold_price' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const sortArrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const totalPages = Math.ceil(filtered.length / perPage);
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const fieldList = [
    'common_name', 'scientific_name', 'genus', 'sale_type', 'sale_size',
    'tissue_culture_sale_pack_size', 'sold_price', 'date_sold', 'source',
    'city', 'state', 'country', 'variegation_level', 'variegation_pattern',
    'submitter_name', 'submitter_email', 'status',
  ];

  const inputStyle = {
    width: '100%', padding: '6px 8px', border: '1.5px solid #c4a882', borderRadius: '6px',
    fontSize: '12px', fontFamily: 'Lato, sans-serif', boxSizing: 'border-box',
  };

  const thStyle = {
    padding: '6px 8px', color: '#f5f0e8', fontFamily: 'Lato, sans-serif',
    fontSize: '10px', letterSpacing: '0.5px', textTransform: 'uppercase',
    textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap',
    cursor: 'pointer', userSelect: 'none',
  };

  const tdStyle = {
    padding: '6px 8px', fontSize: '11px', fontFamily: 'Lato, sans-serif',
    color: '#1e2d1f', whiteSpace: 'nowrap', borderBottom: '1px solid rgba(143,185,154,0.15)',
  };

  if (loading) return <p style={{ textAlign: 'center', padding: '60px', fontFamily: 'Lato, sans-serif', color: '#4a7c59' }}>Loading plant database...</p>;

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', color: '#1e3d20', marginBottom: '8px' }}>🗄️ Full Plant Database</h2>
        <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '16px' }}>
          {filtered.length} of {plants.length} total records. Click any row to edit.
        </p>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            placeholder="Search name, genus, scientific name..."
            style={{ padding: '8px 14px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', minWidth: '240px' }}
          />
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: '8px 14px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', background: '#fff' }}>
            <option value="">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={fetchPlants} style={{ padding: '8px 16px', background: '#f0f7f1', border: '1px solid #4a7c59', color: '#4a7c59', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
            🔄 Refresh
          </button>
          <select value={`${sortField}_${sortDir}`} onChange={(e) => { const [f, d] = e.target.value.split('_'); setSortField(f); setSortDir(d); setPage(1); }}
            style={{ padding: '8px 14px', border: '2px solid #c4a882', borderRadius: '10px', fontSize: '13px', fontFamily: 'Lato, sans-serif', background: '#fff' }}>
            <option value="id_desc">Newest First</option>
            <option value="id_asc">Oldest First</option>
            <option value="common_name_asc">Name A-Z</option>
            <option value="common_name_desc">Name Z-A</option>
            <option value="sold_price_desc">Price High-Low</option>
            <option value="sold_price_asc">Price Low-High</option>
            <option value="date_sold_desc">Sale Date Newest</option>
            <option value="date_sold_asc">Sale Date Oldest</option>
          </select>
        </div>
      </div>

      {editingId && (
        <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 16px rgba(44,74,46,0.1)', marginBottom: '20px', border: '2px solid #4a7c59' }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20', marginBottom: '16px' }}>✏️ Editing Record #{editingId}</h3>

          {/* Photo upload/replace/remove */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div style={{ flexShrink: 0 }}>
              {editForm.image_url ? (
                <img src={editForm.image_url} alt="plant" style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '12px', border: '2px solid rgba(143,185,154,0.4)' }} />
              ) : (
                <div style={{ width: '120px', height: '120px', borderRadius: '12px', background: '#f7f3ec', border: '2px dashed #c4a882', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '28px' }}>🌿</span>
                  <span style={{ fontSize: '10px', color: '#8b9e8c', fontFamily: 'Lato, sans-serif' }}>No photo</span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', borderRadius: '10px', padding: '10px 18px', cursor: photoUploading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: 'Lato, sans-serif', opacity: photoUploading ? 0.6 : 1 }}>
                <input type="file" accept="image/*" disabled={photoUploading} onChange={(e) => { if (e.target.files[0]) uploadEditPhoto(e.target.files[0]); e.target.value = ''; }} style={{ display: 'none' }} />
                {photoUploading ? '⏳ Uploading...' : editForm.image_url ? '🔄 Replace Photo' : '📸 Upload Photo'}
              </label>
              {editForm.image_url && (
                <button onClick={removeEditPhoto} style={{ background: 'transparent', border: '1px solid #c0392b', color: '#c0392b', borderRadius: '10px', padding: '8px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
                  ✕ Remove Photo
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
            {fieldList.map((field) => (
              <div key={field} style={{ position: field === 'common_name' ? 'relative' : 'static' }}>
                <label style={{ display: 'block', fontSize: '9px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '3px', fontFamily: 'Lato, sans-serif' }}>
                  {field.replace(/_/g, ' ')}
                </label>
                {field === 'status' ? (
                  <select value={editForm[field] || ''} onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })} style={inputStyle}>
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                ) : field === 'common_name' ? (
                  <>
                    <input
                      value={editForm[field] || ''}
                      onChange={(e) => handleNameInputChange(e.target.value)}
                      onFocus={() => editForm.common_name && setShowNameSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowNameSuggestions(false), 150)}
                      autoComplete="off"
                      style={inputStyle}
                    />
                    {showNameSuggestions && nameSuggestions.length > 0 && (
                      <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '2px solid #4a7c59', borderRadius: '8px', listStyle: 'none', margin: '2px 0 0', padding: '4px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 6px 20px rgba(44,74,46,0.2)' }}>
                        {nameSuggestions.map((m) => (
                          <li key={m.common_name}
                            onMouseDown={() => selectNameSuggestion(m)}
                            style={{ padding: '6px 10px', cursor: 'pointer', borderRadius: '6px', fontSize: '12px', fontFamily: 'Lato, sans-serif', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f7f1'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span>{m.common_name}</span>
                            {m.matchedOn === 'scientific' && <span style={{ fontSize: '9px', color: '#8b9e8c', fontStyle: 'italic' }}>sci. name match</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <input value={editForm[field] || ''} onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })} style={inputStyle} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={saveEdit} disabled={saving} style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '10px', padding: '10px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              {saving ? 'Saving...' : '✓ Save Changes'}
            </button>
            <button onClick={() => setEditingId(null)} style={{ background: 'transparent', border: '2px solid #c4a882', color: '#4a5e4b', borderRadius: '10px', padding: '10px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>
              Cancel
            </button>
            <button onClick={() => { deletePlant(editingId); setEditingId(null); }} style={{ background: 'transparent', border: '2px solid #c0392b', color: '#c0392b', borderRadius: '10px', padding: '10px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif', marginLeft: 'auto' }}>
              🗑️ Delete Record
            </button>
          </div>
        </div>
      )}

      <div style={{ borderRadius: '16px', boxShadow: '0 2px 16px rgba(44,74,46,0.08)', width: '100%' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)' }}>
              <th style={thStyle} onClick={() => handleSort('id')}>ID{sortArrow('id')}</th>
              <th style={thStyle}>Photo</th>
              <th style={thStyle} onClick={() => handleSort('common_name')}>Common Name{sortArrow('common_name')}</th>
              <th style={thStyle} onClick={() => handleSort('genus')}>Genus{sortArrow('genus')}</th>
              <th style={thStyle} onClick={() => handleSort('sale_type')}>Type{sortArrow('sale_type')}</th>
              <th style={thStyle} onClick={() => handleSort('sale_size')}>Size{sortArrow('sale_size')}</th>
              <th style={thStyle} onClick={() => handleSort('sold_price')}>Price{sortArrow('sold_price')}</th>
              <th style={thStyle} onClick={() => handleSort('date_sold')}>Date{sortArrow('date_sold')}</th>

              <th style={thStyle} onClick={() => handleSort('status')}>Status{sortArrow('status')}</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((plant, idx) => (
              <tr key={plant.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f9fcf9' }}>
                <td style={tdStyle}>{plant.id}</td>
                <td style={tdStyle}>
                  {plant.image_url
                    ? <img src={plant.image_url} alt="plant" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '6px' }} />
                    : <span style={{ color: '#c4a882' }}>—</span>}
                </td>
                <td style={{ ...tdStyle, fontWeight: 700, color: '#1e3d20', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plant.common_name}</td>
                <td style={tdStyle}>{plant.genus || '—'}</td>
                <td style={tdStyle}>{plant.sale_type || '—'}</td>
                <td style={tdStyle}>{plant.sale_size ? `${plant.sale_size}"` : '—'}</td>
                <td style={{ ...tdStyle, color: '#4a7c59', fontWeight: 700 }}>{plant.sold_price || '—'}</td>
                <td style={tdStyle}>{plant.date_sold || '—'}</td>

                <td style={tdStyle}>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, background: plant.status === 'approved' ? '#f0f7f1' : plant.status === 'rejected' ? '#fff5f5' : '#fdf8f0', color: plant.status === 'approved' ? '#4a7c59' : plant.status === 'rejected' ? '#c0392b' : '#8b6914' }}>
                    {plant.status}
                  </span>
                </td>
                <td style={tdStyle}>
                  <button onClick={() => startEdit(plant)} style={{ padding: '4px 10px', background: '#f0f7f1', border: '1px solid #4a7c59', color: '#4a7c59', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', alignItems: 'center' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '8px 16px', background: page === 1 ? '#eee' : '#f0f7f1', border: '1px solid #4a7c59', color: page === 1 ? '#999' : '#4a7c59', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: page === 1 ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif' }}>← Prev</button>
          <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '8px 16px', background: page === totalPages ? '#eee' : '#f0f7f1', border: '1px solid #4a7c59', color: page === totalPages ? '#999' : '#4a7c59', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: page === totalPages ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif' }}>Next →</button>
        </div>
      )}
    </div>
  );
};

export default PlantDatabase;